<template>
  <!-- Loading -->
  <div class="claim-detail-loading-bar progress" v-if="isLoading">
    <div class="indeterminate"></div>
  </div>

  <!-- Display claims -->
  <template v-else>

    <!-- Top -->
    <div id="top"></div>

    <div class="card claim-detail-card yellow lighten-5">
      <div class="card-content white">
        <table class="highlight responsive-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name<br /><small>(Enrollee ID)</small></th>
              <th>Diagnosis</th>
              <th>Sent</th>
              <th>Vetted</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <!-- Claims Summary -->
            <template v-for="(encounter, index) in encounters" :key="encounter.id">
              <tr class="clickable" @click.prevent="handleShow(encounter)">
                <template v-if="encounter.status == 'd'">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <s>{{ capWords(encounter.name) }}</s><br />
                    <s>({{ encounter.enrollee_number }})</s>
                    <div class="claim-review-markers" v-if="encounter.reviewMarkers?.length">
                      <span
                        v-for="marker in encounter.reviewMarkers"
                        :key="`${encounter.id}-${marker.type}`"
                        class="claim-review-marker"
                        :class="`is-${marker.type}`"
                        :title="marker.title"
                      >
                        <i class="material-icons">{{ marker.icon }}</i>
                        <span>{{ marker.label }}</span>
                      </span>
                    </div>
                  </td>
                  <td>
                    <s>{{ capWords(encounter.diagnosis) }}</s>
                  </td>
                  <td>
                    <s>
                      ₦{{
                        Number(encounter.sent)
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }}
                    </s>
                  </td>
                  <td>
                    <s>₦{{ totalApproved(encounter) }}</s>
                  </td>
                  <td>
                    <i class="material-icons grey-text text-lighten-2 pointable" title="removed">do_not_disturb
                    </i>
                  </td>
                </template>

                <template v-else>
                  <td>{{ index + 1 }}</td>
                  <td>
                    {{ capWords(encounter.name) }}<br />
                    ({{ encounter.enrollee_number }})
                    <div class="claim-review-markers" v-if="encounter.reviewMarkers?.length">
                      <span
                        v-for="marker in encounter.reviewMarkers"
                        :key="`${encounter.id}-${marker.type}`"
                        class="claim-review-marker"
                        :class="`is-${marker.type}`"
                        :title="marker.title"
                      >
                        <i class="material-icons">{{ marker.icon }}</i>
                        <span>{{ marker.label }}</span>
                      </span>
                    </div>
                  </td>
                  <td>{{ capWords(encounter.diagnosis) }}</td>
                  <td>₦{{ totalSent(encounter) }}</td>
                  <td>₦{{ totalApproved(encounter) }}</td>
                  <td @click.stop.prevent="handleFlag(encounter)">
                    <i class="material-icons clickable" :class="{
                      'grey-text text-lighten-2':
                        encounter.hod_flag == 'n',
                      'red-text': encounter.hod_flag == 'y',
                    }" :title="getTitle(encounter)">flag
                    </i>
                  </td>
                </template>
              </tr>

              <!-- Claims details -->
              <template v-if="currEncounter?.id == encounter.id">
                <tr v-if="encounter.reviewMarkers?.length">
                  <td></td>
                  <td colspan="5">
                    <div class="claim-review-marker-detail">
                      <span
                        v-for="marker in encounter.reviewMarkers"
                        :key="`detail-${encounter.id}-${marker.type}`"
                        class="claim-review-detail-note"
                        :class="`is-${marker.type}`"
                      >
                        <i class="material-icons">{{ marker.icon }}</i>
                        <span>{{ marker.detail }}</span>
                      </span>
                    </div>
                  </td>
                </tr>
                <tr v-for="claim in encounter.claims" :key="claim.id">
                  <td><br /></td>
                  <td><br /></td>
                  <td class="secondary-content black-text">
                    <span v-if="claim.status == 'd' || encounter.status == 'd'"><s>{{ claim.item }}</s></span>
                    <span v-else>{{ claim.item }}</span>
                  </td>
                  <td class="orange-text text-darken-2">
                    <span v-if="claim.status == 'd' || encounter.status == 'd'"><s>₦{{
                      Number(claim.sent_price)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }}
                      </s></span>
                    <span v-else>₦{{
                      Number(claim.sent_price)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }}
                      <span>({{ claim.sent_qty }})</span></span>
                  </td>
                  <td class="cyan-text text-darken-4">
                    <span v-if="claim.status == 'd' || encounter.status == 'd'"><s>₦{{
                      Number(claim.approved_price)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }}
                      </s></span>
                    <span v-else>₦{{
                      Number(claim.approved_price)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }}
                      <span>({{ claim.approved_qty }})</span></span>
                  </td>
                  <td>
                    <br />
                  </td>
                </tr>
              </template>
            </template>

            <!-- Final total -->
            <tr>
              <td colspan="2"><br /></td>
              <td>Total</td>
              <td>
                <b>₦{{ grossTotalSent(encounters) }}</b>
              </td>
              <td class="green-text text-darken-2">
                <h5>₦{{ grossTotalApproved(encounters) }}</h5>
              </td>
              <td colspan="1"><br /></td>
            </tr>
          </tbody>
        </table>

        <!-- Submit -->
        <div class="review-action-bar">
          <div class="review-action-copy">
            <span>{{ encounters.filter((x) => x.hod_flag == 'y').length ? "Flagged review" : "Ready to pass" }}</span>
            <small>{{ encounters.filter((x) => x.hod_flag == 'y').length ? "Return the marked encounters for correction." : "No HOD flags remain in this batch." }}</small>
          </div>
          <button type="button" class="review-action-btn is-return" :class="{
            disabled: isTalking || isSubmitting,
          }" @click="handleSubmit('return')" v-if="encounters.filter((x) => x.hod_flag == 'y').length">
            <i class="material-icons">keyboard_return</i>
            {{ isSubmitting ? "Returning Claims" : "Request (Flagged) Re-vet" }}
          </button>
          <button type="button" class="review-action-btn is-approve" :class="{
            disabled: isTalking || isSubmitting,
          }" @click="handleSubmit('approve')" v-else>
            <span v-if="payer === 'gov'">
              <i class="material-icons">check_circle</i>{{ isSubmitting ? "Completing" : "Mark as Complete" }}
            </span>
            <span v-else>
              <i class="material-icons">check_circle</i>{{ isSubmitting ? "Passing Claims" : "Pass Claims (To Compliance)" }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import {
  totalSentFx,
  totalApprovedFx,
  grossTotalSentFx,
  grossTotalApprovedFx,
} from "@/composables/calFunction/cal";
import {
  capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";
import { decoratedClaimEncounters } from "@/composables/claimEncounterMarkers";
import getItemsOfItems from "@/composables/getItemsOfItems";
import putItem from "@/composables/putItem";
import nativeUi from "@/ui/native-ui";


export default {
  name: "SingleClaimComponent",
  props: ["clusterId", "payer"],
  emits: ["reset"],
  setup(props, context) {
    const response = ref(null);
    const currEncounter = ref(false);
    const error = ref(null);
    const isTalking = ref(0);
    const isReady = ref(false);
    const isSubmitting = ref(false);
    const isLoading = ref(true);
    const currUrl = ref(null);
    const encounterId = ref(0);

    // get title
    const getTitle = (val) => {
      if (val.hod_flag == "n") {
        return "Flag claim";
      } else {
        return "Un-flag";
      }
    };

    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // total calculations (gross sent)
    const grossTotalSent = (encounters) => {
      return grossTotalSentFx(encounters);
    };

    // total calculations (gross approved)
    const grossTotalApproved = (encounters, db) => {
      return grossTotalApprovedFx(encounters, db);
    };

    // total calculations (sent)
    const totalSent = (encounter) => {
      return totalSentFx(encounter);
    };

    // total calculations (approved)
    const totalApproved = (encounter) => {
      return totalApprovedFx(encounter);
    };

    // get function
    const getItemsFromApi = (table, id) => {
      (async () => {
        const { res, err } = await getItemsOfItems(table, id);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    // starter
    onMounted(() => {
      // set current Url
      currUrl.value = "encounter/cluster";
      // get items from function
      getItemsFromApi(currUrl.value, props.clusterId);
    });

    // show claims
    const handleShow = (val) => {
      if (currEncounter.value == val) {
        currEncounter.value = null;
      } else {
        currEncounter.value = val;
      }
    };

    // flag
    const handleFlag = async (val) => {
      if (isTalking.value) {
        return;
      }

      // talk care of multiple clicks
      isTalking.value++;

      const oldFlag = val.hod_flag;
      const oldComment = val.hod_comment;
      const oldStatus = val.status;

      // set new flag
      let newFlag = "y";
      let newComment = "HOD flagged this Encounter for re-vetting.";
      let newStatus = "p";
      if (val.hod_flag == newFlag) {
        newFlag = "n";
        newComment = null;
        newStatus = "v";
      }

      // update server
      let newVal = {
        hod_flag: newFlag,
        hod_comment: newComment,
        status: newStatus,
      };

      // update first (twitter style)
      response.value.forEach((x, y) => {
        if (x.id == val.id) {
          return (response.value[y] = {
            ...x,
            hod_flag: newFlag,
            hod_comment: newComment,
            status: newStatus,
          });
        }
      });

      // send reset value to parent
      context.emit("reset", {
        clusterId: props.clusterId,
        encounterId: val.id,
        oldFlag: oldFlag,
        newFlag: newFlag,
        newComment: newComment,
        newStatus: newStatus,
      });

      // put update
      const { res, err } = await putItem("encounter", val.id, newVal);

      if (res.value) {
        // clear show claims
        currEncounter.value = null;
        // talk care of multiple clicks
        isTalking.value--;
      } else {
        response.value.forEach((x, y) => {
          if (x.id == val.id) {
            return (response.value[y] = {
              ...x,
              hod_flag: oldFlag,
              hod_comment: oldComment,
              status: oldStatus,
            });
          }
        });

        context.emit("reset", {
          clusterId: props.clusterId,
          encounterId: val.id,
          oldFlag: newFlag,
          newFlag: oldFlag,
          newComment: oldComment,
          newStatus: oldStatus,
        });

        error.value = err.value;
        isTalking.value--;
      }
    };

    // review
    const handleSubmit = async (val) => {
      // start
      isSubmitting.value = true;

      // set update
      let newVal = null;

      if (val == "approve") {
        // add paymentId on server
        newVal = {
          status: "v", // leave as usual (for safeguard)
        };
      } else if (val == "return") {
        newVal = {
          status: "p", // change to "p (pending)"
        };
      }

      // put update
      const { res, err } = await putItem(
        "cluster/hod/review",
        props.clusterId,
        newVal
      );

      if (res.value) {
        // notify (Toast)
          nativeUi.toast({
            html: val == "return" ? "Claim returned <i class='material-icons'>keyboard_return</i>" : "Claim passed <i class='material-icons'>check_circle</i>",
            classes: val == "return" ? "black" : "green",
            displayLength: 5000, // Duration in milliseconds
            inDuration: 300, // Transition in duration
            outDuration: 300, // Transition out duration
          });
        // send reset value to parent
        context.emit("reset", "reload");
      } else {
        error.value = err.value;
      }

      // end submit
      isSubmitting.value = false;
    };

    // output results
    const encounters = computed(() => {
      if (!response.value) {
        return [];
      }

      return decoratedClaimEncounters(response.value);
    });

    return {
      handleShow,
      handleFlag,
      handleSubmit,
      capWords,
      grossTotalSent,
      grossTotalApproved,
      totalApproved,
      totalSent,
      getTitle,
      isReady,
      isTalking,
      isLoading,
      isSubmitting,
      currEncounter,
      error,
      encounterId,
      encounters,
    };
  },
};
</script>

<style scoped>
.custom-hr {
  width: 50%;
  /* Adjust the width as needed */
  border: none;
  border-top: 2px solid #ccc;
  /* Adjust the color and style as needed */
  margin: 20px 20px;
  /* Adjust margin as needed */
}

.claim-detail-loading-bar {
  border-radius: 0 0 6px 6px;
  height: 3px;
  margin: 0 0 12px;
  overflow: hidden;
}

.styled-text {
  background-color: #fafad2;
  /* Light Golden Yellow */
  /* border: 2px solid #000; Black border */
  padding: 2px;
  /* Adequate padding */
}

.capsule {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 20px;
  background-color: #f0f0f0;
  /* Adjust the background color as needed */
}

.claim-detail-card {
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: none;
  overflow: hidden;
}

.claim-detail-card .card-content {
  padding: 24px;
}

.review-action-bar {
  align-items: center;
  background: #f8faf9;
  border-top: 1px solid #e3ece9;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin: 2px -24px -24px;
  padding: 16px 24px;
}

.review-action-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.review-action-copy span {
  color: #263238;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.3;
}

.review-action-copy small {
  color: #607d8b;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.35;
  margin-top: 3px;
}

.review-action-btn {
  align-items: center;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.86rem;
  font-weight: 700;
  gap: 8px;
  justify-content: center;
  min-height: 38px;
  padding: 0 16px;
}

.review-action-btn span {
  align-items: center;
  display: inline-flex;
  gap: 8px;
}

.review-action-btn i {
  font-size: 19px;
}

.review-action-btn.is-approve {
  background: #00796b;
}

.review-action-btn.is-return {
  background: #263238;
}

.review-action-btn.disabled,
.review-action-btn:disabled {
  cursor: default;
  opacity: 0.62;
  pointer-events: none;
}

.clickable {
  cursor: pointer;
}

.clickable:hover {
  color: #000000 !important;
}

.claim-review-markers {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 7px;
}

.claim-review-marker {
  align-items: center;
  border: 1px solid #c4d3d9;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.68rem;
  font-weight: 800;
  gap: 4px;
  line-height: 1.2;
  padding: 4px 7px;
  text-transform: uppercase;
}

.claim-review-marker i {
  font-size: 14px;
  line-height: 14px;
}

.claim-review-marker.is-duplicate,
.claim-review-detail-note.is-duplicate {
  background: #fff7e6;
  border-color: #e7b65f;
  color: #6f4b00;
}

.claim-review-marker.is-multiple,
.claim-review-detail-note.is-multiple {
  background: #eef2f4;
  border-color: #b0bec5;
  color: #37474f;
}

.claim-review-marker-detail {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 8px 0;
}

.claim-review-detail-note {
  align-items: flex-start;
  border: 1px solid #c4d3d9;
  border-radius: 8px;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 8px;
  line-height: 1.45;
  max-width: 760px;
  padding: 8px 10px;
}

.claim-review-detail-note i {
  flex: 0 0 auto;
  font-size: 17px;
  line-height: 1.35;
}

.pointable {
  cursor: default;
  opacity: 1;
  /* Default opacity */
  transition: opacity 0.3s ease;
  /* Smooth opacity change */
}

.pointable:hover {
  opacity: 0.8;
  /* Slightly reduced opacity on hover */
}

.info-item:hover .material-icons.grey-text,
.info-item:hover .material-icons.grey-text::before {
  color: rgb(0, 0, 0) !important;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

.reset-item:hover .material-icons.grey-text,
.reset-item:hover .material-icons.grey-text::before {
  color: rgb(23, 124, 40) !important;
}

.cancel-item:hover .material-icons.grey-text,
.cancel-item:hover .material-icons.grey-text::before {
  color: rgb(214, 16, 16) !important;
}

@media only screen and (max-width: 600px) {
  .claim-detail-card .card-content {
    padding: 18px;
  }

  .review-action-bar {
    align-items: stretch;
    flex-direction: column;
    margin: 2px -18px -18px;
    padding: 16px 18px;
  }

  .review-action-btn {
    min-height: 42px;
    width: 100%;
  }
}
</style>
